/**
 * Nudge Registry
 *
 * Centralized config for all contextual hints/nudges throughout the app.
 * Each entry defines when/how often a nudge can show, and what i18n keys to use for copy.
 *
 * All nudges must have a unique `id` and use `titleKey`/`messageKey`/`ctaLabelKey`
 * pointing to entries in `admin.json` translations under the `nudges` namespace.
 */

export interface NudgeConfig {
  /** Unique nudge identifier (used for tracking impressions in NudgeDeliveryStore) */
  id: string;

  /** Page/section where this nudge appears (informational, for organizing in registry) */
  page: string;

  /** Priority when multiple nudges compete for a slot (higher = more urgent) */
  priority: number;

  /** Max times this nudge should be shown to the user */
  maxShows: number;

  /** Cooldown between impressions (ms). Default: 1 hour */
  cooldownMs?: number;

  /** UI variant (affects colors) */
  variant?: 'default' | 'success' | 'warning' | 'info';

  /** i18n key for nudge title (e.g. 'nudges.dashboard.booking_link_share.title') */
  titleKey: string;

  /** i18n key for nudge message body */
  messageKey: string;

  /** Optional i18n key for CTA button label. If omitted, "Got it" is used. */
  ctaLabelKey?: string;
}

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const NUDGE_REGISTRY: NudgeConfig[] = [
  // ─────────────────────────────────────────────────────────────────────────
  // Dashboard / Onboarding Complete
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'booking_link_share',
    page: 'dashboard/booking-card',
    priority: 10,
    maxShows: 3,
    cooldownMs: ONE_HOUR_MS,
    variant: 'info',
    titleKey: 'nudges.dashboard.booking_link_share.title',
    messageKey: 'nudges.dashboard.booking_link_share.message',
    ctaLabelKey: 'nudges.dashboard.booking_link_share.cta',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Catalog (Phase 2)
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'catalog_add_first_service',
    page: 'catalog',
    priority: 9,
    maxShows: 3,
    cooldownMs: ONE_HOUR_MS,
    variant: 'info',
    titleKey: 'nudges.catalog.add_first_service.title',
    messageKey: 'nudges.catalog.add_first_service.message',
    ctaLabelKey: 'nudges.catalog.add_first_service.cta',
  },
  {
    id: 'catalog_use_template',
    page: 'catalog',
    priority: 8,
    maxShows: 2,
    cooldownMs: ONE_HOUR_MS,
    variant: 'info',
    titleKey: 'nudges.catalog.use_template.title',
    messageKey: 'nudges.catalog.use_template.message',
    ctaLabelKey: 'nudges.catalog.use_template.cta',
  },
  {
    id: 'catalog_deposit_rules',
    page: 'catalog',
    priority: 7,
    maxShows: 2,
    cooldownMs: ONE_DAY_MS,
    variant: 'warning',
    titleKey: 'nudges.catalog.deposit_rules.title',
    messageKey: 'nudges.catalog.deposit_rules.message',
    ctaLabelKey: 'nudges.catalog.deposit_rules.cta',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Calendar & Schedule (Phase 3)
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'calendar_first_appointment',
    page: 'calendar',
    priority: 9,
    maxShows: 3,
    cooldownMs: ONE_HOUR_MS,
    variant: 'info',
    titleKey: 'nudges.calendar.first_appointment.title',
    messageKey: 'nudges.calendar.first_appointment.message',
    ctaLabelKey: 'nudges.calendar.first_appointment.cta',
  },
  {
    id: 'calendar_block_time_off',
    page: 'calendar',
    priority: 6,
    maxShows: 2,
    cooldownMs: ONE_DAY_MS,
    variant: 'info',
    titleKey: 'nudges.calendar.block_time_off.title',
    messageKey: 'nudges.calendar.block_time_off.message',
    ctaLabelKey: 'nudges.calendar.block_time_off.cta',
  },
  {
    id: 'schedule_set_working_hours',
    page: 'schedule',
    priority: 9,
    maxShows: 3,
    cooldownMs: ONE_HOUR_MS,
    variant: 'info',
    titleKey: 'nudges.schedule.set_working_hours.title',
    messageKey: 'nudges.schedule.set_working_hours.message',
    ctaLabelKey: 'nudges.schedule.set_working_hours.cta',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Clients & Messages (Phase 4)
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'clients_segment_filters',
    page: 'clients',
    priority: 5,
    maxShows: 2,
    cooldownMs: ONE_DAY_MS,
    variant: 'info',
    titleKey: 'nudges.clients.segment_filters.title',
    messageKey: 'nudges.clients.segment_filters.message',
    ctaLabelKey: 'nudges.clients.segment_filters.cta',
  },
  {
    id: 'clients_tags',
    page: 'clients',
    priority: 4,
    maxShows: 2,
    cooldownMs: ONE_DAY_MS,
    variant: 'info',
    titleKey: 'nudges.clients.tags.title',
    messageKey: 'nudges.clients.tags.message',
    ctaLabelKey: 'nudges.clients.tags.cta',
  },
  // ─────────────────────────────────────────────────────────────────────────
  // Providers & Settings (Phase 5)
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'providers_invite_team',
    page: 'providers',
    priority: 8,
    maxShows: 2,
    cooldownMs: ONE_DAY_MS,
    variant: 'success',
    titleKey: 'nudges.providers.invite_team.title',
    messageKey: 'nudges.providers.invite_team.message',
    ctaLabelKey: 'nudges.providers.invite_team.cta',
  },
  {
    id: 'settings_connect_payments',
    page: 'settings',
    priority: 10,
    maxShows: 3,
    cooldownMs: ONE_HOUR_MS,
    variant: 'success',
    titleKey: 'nudges.settings.connect_payments.title',
    messageKey: 'nudges.settings.connect_payments.message',
    ctaLabelKey: 'nudges.settings.connect_payments.cta',
  },
  {
    id: 'settings_booking_personality',
    page: 'settings',
    priority: 5,
    maxShows: 2,
    cooldownMs: ONE_DAY_MS,
    variant: 'info',
    titleKey: 'nudges.settings.booking_personality.title',
    messageKey: 'nudges.settings.booking_personality.message',
    ctaLabelKey: 'nudges.settings.booking_personality.cta',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Reports & Profile (Phase 6)
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'reports_export_csv',
    page: 'reports',
    priority: 3,
    maxShows: 2,
    cooldownMs: ONE_DAY_MS,
    variant: 'info',
    titleKey: 'nudges.reports.export_csv.title',
    messageKey: 'nudges.reports.export_csv.message',
    ctaLabelKey: 'nudges.reports.export_csv.cta',
  },
  // ─────────────────────────────────────────────────────────────────────────
  // Settings extras (location, loyalty)
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'settings_add_location',
    page: 'settings',
    priority: 6,
    maxShows: 2,
    cooldownMs: ONE_DAY_MS,
    variant: 'info',
    titleKey: 'nudges.settings.add_location.title',
    messageKey: 'nudges.settings.add_location.message',
    ctaLabelKey: 'nudges.settings.add_location.cta',
  },
  {
    id: 'settings_loyalty_program',
    page: 'settings',
    priority: 5,
    maxShows: 2,
    cooldownMs: ONE_DAY_MS,
    variant: 'success',
    titleKey: 'nudges.settings.loyalty_program.title',
    messageKey: 'nudges.settings.loyalty_program.message',
    ctaLabelKey: 'nudges.settings.loyalty_program.cta',
  },
];

/**
 * Look up a nudge by ID.
 * Useful for useContextualNudge to get the full config.
 */
export function getNudgeConfig(nudgeId: string): NudgeConfig | undefined {
  return NUDGE_REGISTRY.find((n) => n.id === nudgeId);
}
